/* The page's whole type system lives here.
 *
 * Four steps, with real gaps between them — display / statement / body / note.
 * Anything that needs a fifth size is a sign the section is doing too much.
 *
 * Weight policy: semibold belongs to `Display` and to at most one emphasis
 * phrase per section (`<Em>`). Everything else is regular, including the
 * statement tier — size carries the emphasis, not weight. Lists in particular
 * are never bold; a page of bold lists is what "crowded" looks like.
 */

const TONE = {
  light: {
    strong: 'text-[var(--ink)]',
    body: 'text-[var(--ink-2)]',
    faint: 'text-[var(--faint)]',
    rule: 'bg-[var(--ink-blue)]',
  },
  dark: {
    strong: 'text-[#F5F5F3]',
    body: 'text-[rgba(245,245,243,.72)]',
    faint: 'text-[rgba(245,245,243,.5)]',
    rule: 'bg-[#5B8CFF]',
  },
}

export function Display({ as: Tag = 'h2', tone = 'light', className = '', children }) {
  return (
    <Tag
      className={`text-[clamp(2.3rem,4.7vw,3.5rem)] leading-[1.04] font-semibold tracking-[-0.035em] ${TONE[tone].strong} ${className}`}
    >
      {children}
    </Tag>
  )
}

export function Statement({ as: Tag = 'p', tone = 'light', className = '', children }) {
  return (
    <Tag
      className={`text-[clamp(1.55rem,3vw,2.3rem)] leading-[1.22] font-normal tracking-[-0.022em] ${TONE[tone].strong} ${className}`}
    >
      {children}
    </Tag>
  )
}

export function Body({ tone = 'light', className = '', children }) {
  return <p className={`text-[1.125rem] leading-[1.68] ${TONE[tone].body} ${className}`}>{children}</p>
}

export function Note({ tone = 'light', className = '', children }) {
  return (
    <p className={`font-mono text-[12px] leading-[1.7] tracking-[0.06em] ${TONE[tone].faint} ${className}`}>{children}</p>
  )
}

/* The one emphasis phrase a section is allowed. */
export function Em({ tone = 'light', children }) {
  return <b className={`font-semibold ${TONE[tone].strong}`}>{children}</b>
}

export function Accent({ tone = 'light', children }) {
  return <span className={tone === 'dark' ? 'text-[#5B8CFF]' : 'text-[var(--ink-blue)]'}>{children}</span>
}

/* A short accent rule as the marker rather than an arrow or bullet — quieter at
 * the ten-item lengths this page runs to, and it doesn't repeat a glyph the eye
 * has to re-read on every line. */
export function Points({ items, cols = 1, tone = 'light', className = '' }) {
  const grid = cols === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : cols === 2 ? 'sm:grid-cols-2' : ''
  return (
    <ul className={`grid grid-cols-1 gap-x-12 gap-y-4 ${grid} ${className}`}>
      {items.map((item) => (
        <li key={item} className={`flex gap-3.5 text-[1.05rem] leading-[1.6] ${TONE[tone].strong}`}>
          <span aria-hidden="true" className={`mt-[0.68em] h-px w-3.5 flex-none ${TONE[tone].rule}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}
